import React, { useEffect, useState } from 'react';
import { CreditCard, User, Settings, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { stripeService } from '../../services/stripe';

export const ProfilePage: React.FC = () => {
  const { user, firebaseUser, subscriptionStatus, loading, refreshSubscriptionStatus } = useAuth();
  const [isManagingSubscription, setIsManagingSubscription] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);

  // On return from Stripe (success/cancel), refresh status once
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const subResult = params.get('subscription');
    if (subResult === 'success' || subResult === 'cancelled') {
      refreshSubscriptionStatus?.();
      // Clean the query params to avoid repeated refresh
      const url = new URL(window.location.href);
      url.searchParams.delete('subscription');
      window.history.replaceState({}, '', url.toString());
    }
  }, [refreshSubscriptionStatus]);

  const handleManageSubscription = async () => {
    if (!firebaseUser) {
      alert('Please sign in to manage your subscription.');
      return;
    }

    try {
      setIsManagingSubscription(true);
      const token = await firebaseUser.getIdToken();
      await stripeService.redirectToCustomerPortal(token);
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      alert('Failed to open subscription management. Please try again.');
    } finally {
      setIsManagingSubscription(false);
    }
  };

  const handleUpgrade = async () => {
    if (!firebaseUser) {
      alert('Please sign in to upgrade.');
      return;
    }

    try {
      setIsUpgrading(true);
      const token = await firebaseUser.getIdToken();
      await stripeService.redirectToCheckout(token);
    } catch (error) {
      console.error('Failed to start checkout:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setIsUpgrading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Please Sign In</h1>
          <p className="text-gray-600">You need to be signed in to access your profile.</p>
        </div>
      </div>
    );
  }

  const isSubscribed = subscriptionStatus?.has_active_subscription || false;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 h-16">
            <button
              onClick={() => window.location.href = '/'}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors duration-200"
              title="Back to Home"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">home</span>
            </button>
            <a href="/" className="flex-shrink-0 hover:opacity-80 transition-opacity">
              <img 
                src="/how-i-met-your-mother-favicon-cropped.png" 
                alt="How I Met Your Mortgage" 
                className="h-10 w-10"
              />
            </a>
            <h1 className="text-xl font-semibold text-gray-900">Profile & Settings</h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* User Info Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{user.display_name || 'User'}</h2>
                  <p className="text-gray-600">{user.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {isSubscribed ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Premium
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <XCircle className="w-3 h-3 mr-1" />
                        Free
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Management */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="w-6 h-6 text-gray-700" />
                <h3 className="text-lg font-semibold text-gray-900">Subscription Management</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">Current Plan</h4>
                    <p className="text-sm text-gray-600">
                      {isSubscribed ? 'Premium Plan - $2/month' : 'Free Plan'}
                    </p>
                  </div>
                  <div className="text-right">
                    {isSubscribed ? (
                      <span className="text-green-600 font-medium">Active</span>
                    ) : (
                      <span className="text-gray-500">Inactive</span>
                    )}
                  </div>
                </div>

                {isSubscribed ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      Manage your subscription, update payment methods, and view billing history.
                    </p>
                    <button
                      onClick={handleManageSubscription}
                      disabled={isManagingSubscription}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Settings className="w-4 h-4" />
                      {isManagingSubscription ? 'Opening...' : 'Manage Subscription'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      Upgrade to Premium for to display up to 6 analyses at once and to save unlimited analyses.
                    </p>
                    <button
                      onClick={handleUpgrade}
                      disabled={isUpgrading}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CreditCard className="w-4 h-4" />
                      {isUpgrading ? 'Redirecting…' : 'Upgrade to Premium'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Member since</span>
                  <span className="text-sm font-medium text-gray-900">
                    {new Date().toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Plan</span>
                  <span className="text-sm font-medium text-gray-900">
                    {isSubscribed ? 'Premium' : 'Free'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Status</span>
                  <span className={`text-sm font-medium ${isSubscribed ? 'text-green-600' : 'text-gray-500'}`}>
                    {isSubscribed ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Help & Support */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Help & Support</h3>
              <div className="space-y-3">
                <a
                  href="mailto:support@howimetyourmortgage.com"
                  className="block text-sm text-blue-600 hover:text-blue-800 transition-colors"
                >
                  Contact Support
                </a>
                <a
                  href="/?tutorial=true"
                  className="block text-sm text-blue-600 hover:text-blue-800 transition-colors"
                >
                  How to Use
                </a>
                <a
                  href="/privacy"
                  className="block text-sm text-blue-600 hover:text-blue-800 transition-colors"
                >
                  Privacy Policy
                </a>
                <a
                  href="/terms"
                  className="block text-sm text-blue-600 hover:text-blue-800 transition-colors"
                >
                  Terms of Service
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
